import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Mail, Send, Users, Eye, ArrowLeft, AlertTriangle,
  CheckCircle, XCircle, History, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMassEmail = () => {
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [includeWithoutAccount, setIncludeWithoutAccount] = useState(true);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [allRecipients, setAllRecipients] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [targetMode, setTargetMode] = useState("all"); // "all" or "selected"
  const [searchTerm, setSearchTerm] = useState("");

  const getAuth = useCallback(() => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  }, [navigate]);

  useEffect(() => {
    fetchHistory();
    fetchRecipients();
  }, []);

  const fetchRecipients = async () => {
    const headers = getAuth();
    if (!headers) return;
    try {
      const res = await axios.get(`${API}/admin/mass-email/recipients`, { headers });
      setAllRecipients(res.data);
    } catch (error) {
      console.error("Error fetching recipients:", error);
    }
  };

  const fetchHistory = async () => {
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.get(`${API}/admin/mass-email/history`, { headers });
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  const handlePreview = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Veuillez remplir le sujet et le message");
      return;
    }

    const headers = getAuth();
    if (!headers) return;

    setPreviewing(true);
    try {
      // Si mode ciblé, on crée une preview locale
      if (targetMode === "selected" && selectedEmails.length > 0) {
        setPreview({
          total_recipients: selectedEmails.length,
          with_account: selectedEmails.length,
          without_account: 0,
          recipients: selectedEmails.map(email => {
            const found = allRecipients.find(r => r.email === email);
            return { email, name: found?.name || email, has_account: true };
          }),
          subject: subject,
          message_preview: message.slice(0, 500)
        });
      } else {
        const response = await axios.post(`${API}/admin/mass-email/preview`, {
          subject,
          message,
          include_customers_without_account: includeWithoutAccount
        }, { headers });
        setPreview(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la prévisualisation");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    const headers = getAuth();
    if (!headers) return;

    setSending(true);
    try {
      const payload = {
        subject,
        message,
        include_customers_without_account: includeWithoutAccount
      };
      
      // Si mode ciblé, ajouter les emails sélectionnés
      if (targetMode === "selected" && selectedEmails.length > 0) {
        payload.target_emails = selectedEmails;
      }
      
      const response = await axios.post(`${API}/admin/mass-email/send`, payload, { headers });
      
      toast.success(response.data.message);
      setShowConfirm(false);
      setPreview(null);
      setSubject("");
      setMessage("");
      setSelectedEmails([]);
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  // Templates
  const templates = [
    {
      name: "🎁 Offre Parrainage",
      subject: "🌸 Gagnez 5€ en parrainant vos proches !",
      message: `<h2 style="color: #e91e63; margin-bottom: 15px;">Bonjour {{prenom}} !</h2>

<p style="font-size: 16px; line-height: 1.6;">
Bonne nouvelle ! Notre <strong>programme de parrainage</strong> est maintenant disponible ! 🎉
</p>

<div style="background: white; padding: 20px; border-radius: 10px; margin: 20px 0;">
  <h3 style="color: #e91e63; margin-top: 0;">Comment ça marche ?</h3>
  <ul style="font-size: 15px; line-height: 1.8;">
    <li><strong>Vous parrainez</strong> : gagnez 5€ après la 1ère livraison de votre filleul</li>
    <li><strong>Votre filleul</strong> : reçoit 5€ dès son inscription</li>
    <li><strong>Bonus</strong> : 1 tirage Sakura offert après 5 parrainages ! 🌸</li>
  </ul>
</div>

<p style="font-size: 16px;">
Connectez-vous à votre espace client pour découvrir votre code parrainage personnel !
</p>

<p style="font-size: 14px; color: #666; margin-top: 20px;">
À très vite,<br>
L'équipe Pressing Sakura 🌸
</p>`
    },
    {
      name: "📢 Nouvelle Zone",
      subject: "🌸 Pressing Sakura arrive à Francheville, Écully et Tassin !",
      message: `<h2 style="color: #e91e63; margin-bottom: 15px;">Bonjour {{prenom}} !</h2>

<p style="font-size: 16px; line-height: 1.6;">
Grande nouvelle ! Pressing Sakura dessert maintenant <strong>Francheville, Écully, Tassin et leurs alentours</strong> ! 🎉
</p>

<div style="background: white; padding: 20px; border-radius: 10px; margin: 20px 0;">
  <h3 style="color: #e91e63; margin-top: 0;">📍 Nouvelles zones : Francheville, Écully, Tassin</h3>
  <p style="font-size: 15px;">Passages tous les <strong>mercredis</strong></p>
</div>

<p style="font-size: 16px;">
N'hésitez pas à en parler autour de vous !
</p>

<p style="font-size: 14px; color: #666; margin-top: 20px;">
À très vite,<br>
L'équipe Pressing Sakura 🌸
</p>`
    },
    {
      name: "⭐ Promotion",
      subject: "🌸 -20% sur votre prochaine prestation !",
      message: `<h2 style="color: #e91e63; margin-bottom: 15px;">Bonjour {{prenom}} !</h2>

<p style="font-size: 16px; line-height: 1.6;">
Bénéficiez de <strong>-20%</strong> sur votre prochaine prestation !
</p>

<div style="background: white; padding: 20px; border-radius: 10px; margin: 20px 0; text-align: center;">
  <h3 style="color: #e91e63; margin-top: 0; font-size: 24px;">🎁 -20% OFFERTS</h3>
  <p style="font-size: 16px;">Précisez-le lors de votre prochain ramassage ou notez-le dans les remarques lors de votre réservation en ligne.</p>
</div>

<p style="font-size: 14px; color: #e91e63; margin-top: 15px; font-weight: bold;">
⏰ Offre valable uniquement ce mois-ci !
</p>
<p style="font-size: 12px; color: #999; font-style: italic;">
* Non applicable sur les packs de chemises.
</p>

<p style="font-size: 14px; color: #666; margin-top: 20px;">
À très vite,<br>
L'équipe Pressing Sakura 🌸
</p>`
    },
    {
      name: "🧥 Promo Doudounes",
      subject: "🌸 -20% sur vos doudounes, blousons et manteaux !",
      message: `<h2 style="color: #e91e63; margin-bottom: 15px;">Bonjour {{prenom}} !</h2>

<p style="font-size: 16px; line-height: 1.6;">
L'hiver approche ! C'est le moment idéal pour faire nettoyer vos <strong>doudounes, blousons et manteaux</strong>.
</p>

<div style="text-align: center; margin: 25px 0;">
  <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/fj0vjdet_Promotion%20pressing%20Sakura%20au%20printemps.png" alt="Promo -20% Doudounes Blousons Manteaux" style="max-width: 100%; border-radius: 15px; box-shadow: 0 4px 15px rgba(0,0,0,0.1);" />
</div>

<div style="background: linear-gradient(135deg, #fce4ec, #f8bbd9); padding: 25px; border-radius: 15px; margin: 20px 0; text-align: center;">
  <h3 style="color: #c2185b; margin-top: 0; font-size: 28px;">🧥 -20% SUR VOS VÊTEMENTS D'HIVER</h3>
  <p style="font-size: 16px; color: #333;">Doudounes • Blousons • Manteaux</p>
  <p style="font-size: 14px; color: #666; margin-top: 10px;">Nettoyage en profondeur • Respect des matières • Redonne confort et éclat</p>
</div>

<div style="background: white; padding: 20px; border-radius: 10px; margin: 20px 0; border-left: 4px solid #e91e63;">
  <h4 style="color: #e91e63; margin-top: 0;">💧 Pensez à l'imperméabilisation !</h4>
  <p style="font-size: 15px; margin-bottom: 0;">Protégez vos vêtements de l'humidité et des taches pour seulement <strong style="color: #e91e63;">3€ par pièce</strong>.</p>
</div>

<p style="font-size: 14px; color: #e91e63; margin-top: 15px; font-weight: bold;">
⏰ Offre valable tout le mois !
</p>

<p style="font-size: 14px; color: #666; margin-top: 20px;">
À très vite,<br>
L'équipe Pressing Sakura 🌸
</p>`
    }
  ];

  const applyTemplate = (template) => {
    setSubject(template.subject);
    setMessage(template.message);
    toast.success(`Template "${template.name}" appliqué`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-pink-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/dashboard")}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Mail className="w-6 h-6 text-pink-600" />
              Email en masse
            </h1>
            <p className="text-slate-500 text-sm">Envoyez des emails à tous vos clients</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Composer votre email</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Templates */}
                <div>
                  <Label className="text-sm text-slate-500">Templates rapides</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {templates.map((t, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        onClick={() => applyTemplate(t)}
                        className="text-xs"
                      >
                        {t.name}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <Label htmlFor="subject">Sujet de l'email</Label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="🌸 Votre sujet ici..."
                    className="mt-1"
                  />
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message">
                    Message (HTML supporté)
                    <span className="text-xs text-slate-400 ml-2">
                      Utilisez {"{{prenom}}"} pour personnaliser
                    </span>
                  </Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="<h2>Bonjour {{prenom}} !</h2>&#10;&#10;<p>Votre message ici...</p>"
                    className="mt-1 min-h-[300px] font-mono text-sm"
                  />
                </div>

                {/* Options */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <Label htmlFor="include-all" className="font-medium">
                      Inclure les clients sans compte
                    </Label>
                    <p className="text-xs text-slate-500">
                      Emails collectés via les réservations
                    </p>
                  </div>
                  <Switch
                    id="include-all"
                    checked={includeWithoutAccount}
                    onCheckedChange={setIncludeWithoutAccount}
                    disabled={targetMode === "selected"}
                  />
                </div>

                {/* Target Mode */}
                <div className="p-3 bg-blue-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-4">
                    <Label className="font-medium">Destinataires :</Label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={targetMode === "all" ? "default" : "outline"}
                        onClick={() => { setTargetMode("all"); setSelectedEmails([]); }}
                      >
                        Tous les clients
                      </Button>
                      <Button
                        size="sm"
                        variant={targetMode === "selected" ? "default" : "outline"}
                        onClick={() => setTargetMode("selected")}
                      >
                        Cibler des clients
                      </Button>
                    </div>
                  </div>
                  
                  {targetMode === "selected" && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Rechercher un client..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="bg-white flex-1"
                        />
                        <span className="text-xs text-slate-500 whitespace-nowrap">{allRecipients.length} clients</span>
                      </div>
                      <div className="max-h-60 overflow-y-auto bg-white rounded border p-2 space-y-1">
                        {allRecipients
                          .filter(r => 
                            r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            r.email.toLowerCase().includes(searchTerm.toLowerCase())
                          )
                          .map(r => (
                            <label key={r.email} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                              <input
                                type="checkbox"
                                checked={selectedEmails.includes(r.email)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedEmails([...selectedEmails, r.email]);
                                  } else {
                                    setSelectedEmails(selectedEmails.filter(em => em !== r.email));
                                  }
                                }}
                                className="rounded"
                              />
                              <span className="font-medium">{r.name}</span>
                              <span className="text-slate-400 text-xs">{r.email}</span>
                            </label>
                          ))}
                      </div>
                      {selectedEmails.length > 0 && (
                        <p className="text-sm text-blue-600 font-medium">
                          {selectedEmails.length} client(s) sélectionné(s)
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={handlePreview}
                    disabled={previewing || !subject || !message || (targetMode === "selected" && selectedEmails.length === 0)}
                    className="flex-1"
                  >
                    {previewing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    Prévisualiser
                  </Button>
                  <Button
                    onClick={() => preview ? setShowConfirm(true) : handlePreview()}
                    disabled={sending || !subject || !message || (targetMode === "selected" && selectedEmails.length === 0)}
                    className="flex-1 bg-pink-600 hover:bg-pink-700"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {preview ? `Envoyer${targetMode === "selected" ? ` à ${selectedEmails.length} client(s)` : ""}` : "Prévisualiser d'abord"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Preview */}
            {preview && (
              <Card className="border-pink-200">
                <CardHeader className="bg-pink-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Eye className="w-5 h-5 text-pink-600" />
                    Prévisualisation
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-slate-800">{preview.total_recipients}</p>
                      <p className="text-xs text-slate-500">Total destinataires</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{preview.with_account}</p>
                      <p className="text-xs text-slate-500">Avec compte</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{preview.without_account}</p>
                      <p className="text-xs text-slate-500">Sans compte</p>
                    </div>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium text-slate-600">Sujet :</p>
                    <p className="text-slate-800">{preview.subject}</p>
                  </div>

                  <div className="p-3 bg-white border rounded-lg">
                    <p className="text-sm font-medium text-slate-600 mb-2">Aperçu du message :</p>
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: preview.message_preview }}
                    />
                  </div>

                  {preview.recipients.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-2">
                        Premiers destinataires ({Math.min(preview.recipients.length, 10)}/{preview.total_recipients}) :
                      </p>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {preview.recipients.slice(0, 10).map((r, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                            <span>{r.name || "Sans nom"}</span>
                            <span className="text-slate-500">{r.email}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Conseils
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-slate-600 space-y-2">
                <p>• Utilisez <code className="bg-slate-100 px-1 rounded">{"{{prenom}}"}</code> pour personnaliser</p>
                <p>• Le HTML est supporté pour le formatage</p>
                <p>• Prévisualisez toujours avant d'envoyer</p>
                <p>• Les emails sont envoyés progressivement</p>
              </CardContent>
            </Card>

            {/* History */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-500" />
                  Historique récent
                </CardTitle>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">Aucun envoi</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {history.slice(0, 5).map((h, idx) => (
                      <div key={idx} className="p-2 bg-slate-50 rounded text-xs">
                        <p className="font-medium truncate">{h.subject}</p>
                        <div className="flex items-center gap-2 mt-1 text-slate-500">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          <span>{h.sent_count} envoyés</span>
                          {h.failed_count > 0 && (
                            <>
                              <XCircle className="w-3 h-3 text-red-500" />
                              <span>{h.failed_count} échoués</span>
                            </>
                          )}
                        </div>
                        <p className="text-slate-400 mt-1">
                          {new Date(h.sent_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Confirm Dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-pink-600" />
              Confirmer l'envoi
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point d'envoyer cet email à <strong>{preview?.total_recipients}</strong> destinataire(s).
              Cette action ne peut pas être annulée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSend}
              disabled={sending}
              className="bg-pink-600 hover:bg-pink-700"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer à {preview?.total_recipients} personnes
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminMassEmail;
